import { Device } from '@/api';
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useDevicesTranslation } from '../hooks';
import { addOrUpdateAllDevicesCache } from '../queries/devices-cache';

type UseCancelDeviceGeoAlertActionProps = {
    deviceId: string;
};

/** Everything the caller chooses per call. */
export type CancelDeviceGeoAlertVariables = {
    /** Which of the device's four geofence slots to leave as it was (0-3). */
    slot: number;
};

type UseCancelDeviceGeoAlertActionData = {
    cancelDeviceGeoAlert: (
        variables: CancelDeviceGeoAlertVariables
    ) => Promise<Device>;
    loading: boolean;
    error: string;
};

/**
 * Withdrawing a geofence change the device has not downloaded yet.
 *
 * No `isAllowed`/`reason`: the gate is the device settings page's own
 * `canEdit`, which it works out once for every action on the device and passes
 * down to the buttons.
 */
export function useCancelDeviceGeoAlertAction(
    props: UseCancelDeviceGeoAlertActionProps
): UseCancelDeviceGeoAlertActionData {
    const { deviceId } = props;

    const { t } = useDevicesTranslation();
    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `DELETE /v3/devices/{deviceId}/config/geofence/{slot}/pending`
    // declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'DEVICE_NOT_FOUND')) {
                return t('shared.errors.device_not_found');
            }
            if (isApiError(error, 'NO_PERMISSION')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                // The server names the offending field; it is more use than
                // anything generic we could write.
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (variables: CancelDeviceGeoAlertVariables) =>
            openApiRef.current.devices
                .cancelDeviceConfigGeoAlert(deviceId, String(variables.slot))
                .then((response) => response.data.data.device),
        onSuccess: (device) => {
            // What comes back no longer carries the pending change, and the
            // lists have to stop showing one.
            addOrUpdateAllDevicesCache(queryClient, device);

            UIToast.success(t('actions.cancel_device_config.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        cancelDeviceGeoAlert: (variables) => mutation.mutateAsync(variables),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
